import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import mixpanel from 'mixpanel-browser';
import { AuthProvider, UrqlProvider } from '@web/api';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import { env } from '@/env';
import { UserProvider } from '@/provider';
import { logoutAtom } from '@/store/atoms';
import ThemeProvider from '@/theme';
import app from '../../package.json';
import { datadogRum } from '@datadog/browser-rum';

if (env.VITE_DATADOG_APPLICATION_ID && env.VITE_DATADOG_CLIENT_TOKEN) {
  datadogRum.init({
    applicationId: env.VITE_DATADOG_APPLICATION_ID,
    clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
    proxyUrl: env.VITE_DATADOG_PROXY,
    site: 'datadoghq.eu',
    service: env.VITE_SRV_NAME,
    allowedTracingOrigins: [env.VITE_GRAPH_API_URL],
    allowedTracingUrls: [
      {
        match: env.VITE_GRAPH_API_URL,
        propagatorTypes: ['datadog', 'tracecontext'],
      },
    ],
    sampleRate: 100,
    sessionReplaySampleRate: 100,
    trackInteractions: true,
    trackFrustrations: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
  datadogRum.startSessionReplayRecording();
}

if (env.VITE_MIXPANEL_TOKEN) {
  mixpanel.init(env.VITE_MIXPANEL_TOKEN, {
    debug: env.VITE_NODE_ENV === 'development' ? false : true,
    track_pageview: true,
    persistence: 'localStorage',
  });
}

export default function App() {
  const [, setLogout] = useAtom(logoutAtom);
  const [searchParams] = useSearchParams();
  const retailer_id = searchParams.get('retailer_id');
  const code = searchParams.get('code');

  useEffect(() => {
    function openAppOrStore() {
      if (!retailer_id || !code) return;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      const storeUrl = isIOS
        ? 'https://apps.apple.com/de/app/agrantis-gmbh/id6445820802'
        : 'https://play.google.com/store/apps/details?id=com.agrantis.app';

      setTimeout(() => {
        window.location.href = storeUrl;
      }, 2000); // Wait for 2 seconds
    }
    openAppOrStore();
  }, [code, retailer_id]);

  return (
    <ThemeProvider>
      <AuthProvider
        authUrl={env.VITE_HANKO_API_URL}
        onLogout={setLogout}
        useNavigate={useNavigate}
      >
        <UrqlProvider
          graphUrl={env.VITE_GRAPH_API_URL}
          usage={{ name: app.name, version: app.version }}
        >
          <UserProvider>
            <Toaster />
            <ScrollToTop />
            <Outlet />
          </UserProvider>
        </UrqlProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
